import { useState, useCallback, useRef, useEffect } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { usePetBreeds } from '@/shared/hooks/queries/use-pet-breeds'
import type { PersonDetails } from '@/components/molecules/person-form-section'
import type { PetDetails } from '@/components/molecules/pet-form-section'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { useAuth } from '@/shared/hooks/use-auth'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import {
  CACHE_STALE_TIME,
  CACHE_GC_TIME,
  QUERY_NETWORK_MODE,
  RENTERS_QUERY_KEYS,
} from '@/shared/constants/cache'
import {
  postAdditionalParties,
  getRentersPolicy,
} from '@/services/renters.services'
import type { AdditionalPartyPayload } from '@/services/renters.services'
import {
  fetchPetAdditionalParty,
  updatePetAdditionalParty,
} from '@/services/petAdditionalParty.services'
import type { PetAdditionalParty } from '@/services/petAdditionalParty.services'
import type { PolicyAdditionalParty } from '@/shared/types/global.types'
import { PET_AGE_OPTIONS } from '@/shared/constants/onboarding'
import { isValidEmail } from '@/shared/helpers/validation'
import type { RentersFormValues } from '../page.onboarding-renters-form.hook'

const FORM_FIELD = {
  ROOMMATES: 'roommates',
  PARTNERS: 'partners',
  PETS: 'pets',
} as const

const RHF_KEY_NAME = '_rhfId'

const ADDITIONAL_PARTY_TYPE = {
  ROOMMATE: 'roommate',
  SPOUSE: 'Spouse',
  PET: 'pet',
} as const

const ANALYTICS_EVENT = 'ob_renters_form_started'

const PET_TYPE_FIELD: keyof PetDetails = 'petType'

export type { PersonDetails, PetDetails }

const PET_AGE_VALUES = PET_AGE_OPTIONS.map((o) => o.value)

const MS_PER_DAY = 24 * 60 * 60 * 1000

function birthDateToAgeDays(birthDate: string): string {
  const diffMs = Date.now() - new Date(birthDate).getTime()
  const diffDays = Math.round(diffMs / MS_PER_DAY)
  return PET_AGE_VALUES.reduce((closest, opt) =>
    Math.abs(Number(opt) - diffDays) < Math.abs(Number(closest) - diffDays)
      ? opt
      : closest,
  )
}

const dateFormatter = new Intl.DateTimeFormat('en-CA')

function formatDateYMD(date: Date): string {
  return dateFormatter.format(date)
}

function ageDaysToBirthDate(ageDays: string): string {
  return formatDateYMD(new Date(Date.now() - Number(ageDays) * MS_PER_DAY))
}

function partyToPetDetails(party: PetAdditionalParty): PetDetails {
  const dateOnly = party.pet_birth_date
    ? formatDateYMD(new Date(party.pet_birth_date))
    : ''
  return {
    id: party.id,
    name: party.name,
    petType: party.pet_type
      ? party.pet_type.charAt(0).toUpperCase() +
        party.pet_type.slice(1).toLowerCase()
      : '',
    age: dateOnly ? birthDateToAgeDays(dateOnly) : '',
    breed: party.breed_id ? String(party.breed_id) : '',
  }
}

function formatAddress(addr: PolicyAdditionalParty['address']): string {
  if (!addr) return ''
  const parts = [
    addr.streetName?.trim() || addr.streetNumber?.trim(),
    addr.city,
    addr.state,
  ].filter(Boolean)
  return parts.join(', ')
}

function partyToPersonDetails(party: PolicyAdditionalParty): PersonDetails {
  return {
    id: party.id,
    name: party.fullName ?? '',
    email: party.email ?? '',
    address: formatAddress(party.address),
  }
}

function emptyPerson(): PersonDetails {
  return { name: '', address: '', email: '' }
}

function emptyPet(): PetDetails {
  return { name: '', petType: '', age: '', breed: '' }
}

export function useRentersFormOne(
  onContinue: () => void,
  onRegisterSubmit?: (fn: () => Promise<void>) => void,
  onDisabledChange?: (disabled: boolean) => void,
  onLoadingChange?: (loading: boolean) => void,
) {
  const { user: authUser, loading } = useAuth()
  const { data: breedsData } = usePetBreeds()
  const breeds = Array.isArray(breedsData) ? breedsData : []

  const { data: rentersPolices, isLoading: isPolicyLoading } = useQuery({
    queryKey: RENTERS_QUERY_KEYS.policies,
    queryFn: async () => {
      const jwt = await getJwt()
      return getRentersPolicy(jwt)
    },
    enabled: !loading && !!authUser,
    staleTime: CACHE_STALE_TIME,
    gcTime: CACHE_GC_TIME,
    networkMode: QUERY_NETWORK_MODE,
  })

  const policyId = rentersPolices?.[0]?.id ?? ''

  const { data: existingParty, isLoading: isAdditionalPartyLoading } = useQuery(
    {
      queryKey: RENTERS_QUERY_KEYS.petAdditionalParty(policyId),
      queryFn: () => fetchPetAdditionalParty(policyId),
      enabled: !loading && !!authUser && !!policyId,
      staleTime: CACHE_STALE_TIME,
      gcTime: CACHE_GC_TIME,
      networkMode: QUERY_NETWORK_MODE,
    },
  )

  const getBreedOptions = useCallback(
    (petType: string) =>
      breeds
        .filter(
          (b) =>
            !b.isExcluded &&
            b.petType.name.toLowerCase() === petType.toLowerCase(),
        )
        .map((b) => ({ value: String(b.id), label: b.name })),
    [breeds],
  )

  const { control, getValues } = useFormContext<RentersFormValues>()

  const {
    fields: roommateFields,
    append: appendRoommate,
    remove: removeRoommateField,
    update: updateRoommateField,
    replace: replaceRoommates,
  } = useFieldArray({
    control,
    name: FORM_FIELD.ROOMMATES,
    keyName: RHF_KEY_NAME,
  })

  const {
    fields: partnerFields,
    append: appendPartner,
    remove: removePartnerField,
    update: updatePartnerField,
    replace: replacePartners,
  } = useFieldArray({
    control,
    name: FORM_FIELD.PARTNERS,
    keyName: RHF_KEY_NAME,
  })

  const {
    fields: petFields,
    append: appendPetField,
    remove: removePetField,
    update: updatePetField,
    replace: replacePets,
  } = useFieldArray({ control, name: FORM_FIELD.PETS, keyName: RHF_KEY_NAME })

  const hasFired = useRef(false)
  const initializedRef = useRef<PetAdditionalParty | undefined>(undefined)
  const personsInitializedRef = useRef(false)
  const originalPetsRef = useRef<Map<string, PetDetails>>(new Map())
  const queryClient = useQueryClient()

  useEffect(() => {
    if (hasFired.current) return
    hasFired.current = true
    sendEvent(ANALYTICS_EVENT)
    sendEvent('ob_renters_form_1')
  }, [])

  useEffect(() => {
    if (!existingParty) return
    if (initializedRef.current === existingParty) return
    initializedRef.current = existingParty

    const mapped = partyToPetDetails(existingParty)
    replacePets([mapped])
    originalPetsRef.current = new Map([[mapped.id!, mapped]])
  }, [existingParty, replacePets])

  useEffect(() => {
    if (personsInitializedRef.current) return
    const parties = rentersPolices?.[0]?.additionalParties
    if (!parties || parties.length === 0) return
    personsInitializedRef.current = true

    const roommates = parties.filter(
      (p) => p.type?.toLowerCase() === ADDITIONAL_PARTY_TYPE.ROOMMATE,
    )
    const partners = parties.filter(
      (p) =>
        p.type?.toLowerCase() === ADDITIONAL_PARTY_TYPE.SPOUSE.toLowerCase(),
    )

    if (roommates.length > 0) {
      replaceRoommates(roommates.map(partyToPersonDetails))
    }
    if (partners.length > 0) {
      replacePartners(partners.map(partyToPersonDetails))
    }
  }, [rentersPolices, replaceRoommates, replacePartners])

  const addRoommate = useCallback(
    () => appendRoommate(emptyPerson()),
    [appendRoommate],
  )
  const removeRoommate = useCallback(
    (index: number) => removeRoommateField(index),
    [removeRoommateField],
  )
  const updateRoommate = useCallback(
    (index: number, field: keyof PersonDetails, value: string) => {
      const current = getValues(FORM_FIELD.ROOMMATES)[index]
      if (!current) return
      updateRoommateField(index, { ...current, [field]: value })
    },
    [getValues, updateRoommateField],
  )

  const addPartner = useCallback(
    () => appendPartner(emptyPerson()),
    [appendPartner],
  )
  const removePartner = useCallback(
    (index: number) => removePartnerField(index),
    [removePartnerField],
  )
  const updatePartner = useCallback(
    (index: number, field: keyof PersonDetails, value: string) => {
      const current = getValues(FORM_FIELD.PARTNERS)[index]
      if (!current) return
      updatePartnerField(index, { ...current, [field]: value })
    },
    [getValues, updatePartnerField],
  )

  const addPet = useCallback(() => appendPetField(emptyPet()), [appendPetField])
  const removePet = useCallback(
    (index: number) => removePetField(index),
    [removePetField],
  )
  const updatePet = useCallback(
    (index: number, field: keyof PetDetails, value: string) => {
      const current = getValues(FORM_FIELD.PETS)[index]
      if (!current) return
      const updated = { ...current, [field]: value }
      if (field === PET_TYPE_FIELD) updated.breed = ''
      updatePetField(index, updated)
    },
    [getValues, updatePetField],
  )

  const isContinueDisabled =
    roommateFields.some(
      (e) =>
        !e.id &&
        (e.name || e.email) &&
        (!e.name || !e.email || !isValidEmail(e.email)),
    ) ||
    petFields.some(
      (e) =>
        (e.name || e.petType || e.age || e.breed) &&
        (!e.name || !e.petType || !e.age || !e.breed),
    )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const handleContinue = useCallback(async () => {
    if (!policyId) {
      onContinue()
      return
    }
    if (isContinueDisabled) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    setIsSubmitting(true)
    const jwt = await getJwt()
    const { roommates, partners, pets } = getValues()

    const newParties: AdditionalPartyPayload[] = []
    const patchTasks: Promise<PetAdditionalParty>[] = []

    for (const entry of roommates) {
      if (entry.id || !entry.name) continue
      const item: AdditionalPartyPayload = {
        type: ADDITIONAL_PARTY_TYPE.ROOMMATE,
        name: entry.name,
      }
      if (entry.email) item.email = entry.email
      newParties.push(item)
    }

    for (const entry of partners) {
      if (entry.id || !entry.name) continue
      newParties.push({ type: ADDITIONAL_PARTY_TYPE.SPOUSE, name: entry.name })
    }

    for (const entry of pets) {
      if (!entry.name) continue
      const birthDate = entry.age ? ageDaysToBirthDate(entry.age) : undefined
      const breedId = entry.breed ? Number(entry.breed) : undefined

      if (entry.id) {
        const original = originalPetsRef.current.get(entry.id)
        const isDirty =
          !original ||
          entry.name !== original.name ||
          entry.petType !== original.petType ||
          entry.age !== original.age ||
          entry.breed !== original.breed
        if (isDirty) {
          const patchBody: Partial<
            Omit<PetAdditionalParty, 'id' | 'kanguro_policy_id'>
          > = { name: entry.name }
          if (entry.petType) patchBody.pet_type = entry.petType
          if (birthDate) patchBody.pet_birth_date = birthDate
          if (breedId) patchBody.breed_id = breedId
          patchTasks.push(updatePetAdditionalParty(entry.id, patchBody))
        }
      } else {
        const postItem: AdditionalPartyPayload = {
          type: ADDITIONAL_PARTY_TYPE.PET,
          name: entry.name,
        }
        if (entry.petType) postItem.pet_type = entry.petType
        if (birthDate) postItem.pet_birth_date = birthDate
        if (breedId) postItem.breed_id = breedId
        newParties.push(postItem)
      }
    }

    const tasks: Promise<PetAdditionalParty | void>[] = [...patchTasks]
    if (newParties.length > 0) {
      tasks.push(postAdditionalParties(jwt, policyId, newParties))
    }

    await Promise.allSettled(tasks)
    await queryClient.invalidateQueries({
      queryKey: RENTERS_QUERY_KEYS.petAdditionalParty(policyId),
    })
    setIsSubmitting(false)
    onContinue()
  }, [policyId, queryClient, isContinueDisabled, getValues, onContinue])

  useEffect(() => {
    onRegisterSubmit?.(handleContinue)
  }, [onRegisterSubmit, handleContinue])

  useEffect(() => {
    onDisabledChange?.(isContinueDisabled)
  }, [onDisabledChange, isContinueDisabled])

  useEffect(() => {
    onLoadingChange?.(isSubmitting)
  }, [onLoadingChange, isSubmitting])

  return {
    onContinue: handleContinue,
    isContinueDisabled,
    roommateEntries: roommateFields,
    addRoommate,
    removeRoommate,
    updateRoommate,
    partnerEntries: partnerFields,
    addPartner,
    removePartner,
    updatePartner,
    petEntries: petFields,
    addPet,
    removePet,
    updatePet,
    getBreedOptions,
    isSubmitting,
    isDataLoading: isPolicyLoading || isAdditionalPartyLoading,
    showErrors,
  }
}
